import type { NriMatrixRow } from 'types/api-responses';
import styles from './nri-matrix-table.module.scss';
import { formatNumberWithTooltip } from 'components/tooltip-number';
import { MatrixTable } from 'components/matrix-table';
import { CalculationType } from 'components/nri-table/types';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { BaseCard } from 'components/base-card';
import {
  nriCalculationIdForMatrixAtom,
  nriMatrixLoadableAtom,
} from 'atoms/nri-matrix-atoms';
import {
  formatAllocationMethod,
  formatValue,
  formatFactor,
} from 'pages/crm-tracts-subtracts/nri/tract-nri-matrix-utils';
import {
  UatCalculationContent,
  UatInterestCell,
} from 'pages/crm-tracts-subtracts/nri/uat-interest-cell';
import { gisRowOf } from './utils';

interface NriMatrixTableProps {
  rows: NriMatrixRow[];
  calculationType?: CalculationType;
}

// No "In Use" column: the row marked `inUse` is highlighted instead.
const getHeaders = (calculationType: CalculationType) => [
  { key: 'allocationMethod', header: 'Allocation Method' },
  { key: 'uat', header: 'UAT' },
  { key: 'value', header: 'Value' },
  { key: 'factor', header: 'Factor' },
  {
    key: 'uatInterest',
    header: calculationType === 'WI' ? 'UAT WI' : 'UAT NRI',
  },
];

export const NriMatrixTable = ({
  rows: originalRows,
  calculationType = 'NRI',
}: NriMatrixTableProps) => {
  const nriMatrixLoadable = useAtomValue(nriMatrixLoadableAtom);
  const matrixData =
    nriMatrixLoadable.state === 'hasData' && nriMatrixLoadable.data
      ? nriMatrixLoadable.data
      : null;
  const formulaInputs = matrixData?.formulaInputs ?? null;

  interface FormattedRow extends NriMatrixRow {
    id: string;
    allocationMethodDisplay: string;
    valueDisplay: React.ReactNode;
    factorDisplay: React.ReactNode;
    uatInterestDisplay: React.ReactNode;
  }

  const headers = getHeaders(calculationType);

  const tableRows: FormattedRow[] = originalRows.map((row, index) => {
    // Use uatNri or uatWi based on calculationType
    const interestValue = calculationType === 'WI' ? row.uatWi : row.uatNri;
    const formattedRow: FormattedRow = {
      id: String(index),
      ...row,
      // Format display values
      allocationMethodDisplay: formatAllocationMethod(
        row.allocationMethod,
        row.source
      ),
      valueDisplay: formatValue(row.value, row.allocationMethod),
      factorDisplay: formatFactor(row.factor),
      uatInterestDisplay:
        interestValue !== null
          ? formatNumberWithTooltip(interestValue, 8)
          : '--',
    };
    return formattedRow;
  });

  const renderUATInterestCell = (
    row: NriMatrixRow,
    formattedRow: FormattedRow
  ) => {
    const interestValue = calculationType === 'WI' ? row.uatWi : row.uatNri;

    return (
      <UatInterestCell
        interestValue={interestValue}
        displayValue={formattedRow.uatInterestDisplay}
        formulaInputs={formulaInputs}
        allocationMethod={row.allocationMethod}
        source={row.source}
        rowValue={row.value}
        calculationType={calculationType}
        cellClassName={styles.uatNriCell}
        cardContentClassName={styles.calculationCardContent}
        iconContainerClassName={styles.iconContainer}
      />
    );
  };

  /**
   * One matrix cell's content.
   *
   * @param {string} columnKey - The cell's column.
   * @param {number} rowIndex - The cell's row.
   * @return {React.ReactNode} The content.
   */
  const renderCell = (columnKey: string, rowIndex: number) => {
    const rowData = originalRows[rowIndex];
    const formattedRow = tableRows[rowIndex];
    if (!rowData || !formattedRow) return null;

    switch (columnKey) {
      case 'allocationMethod':
        return formattedRow.allocationMethodDisplay;
      case 'value':
        return formattedRow.valueDisplay ?? '--';
      case 'factor':
        return formattedRow.factorDisplay ?? '--';
      case 'uatInterest':
        return renderUATInterestCell(rowData, formattedRow);
      default:
        return formattedRow.uat;
    }
  };

  return (
    <MatrixTable
      className={styles.tableWrapper}
      rows={tableRows}
      columns={headers}
      renderCell={renderCell}
      isRowHighlighted={(rowIndex) => originalRows[rowIndex]?.inUse ?? false}
    />
  );
};

interface GisCalculationCardProps {
  nriCalculationId: number;
  calculationType: CalculationType;
  renderTrigger: (active: boolean, toggle: () => void) => React.ReactNode;
}

/**
 * The GIS row's calculation from the calculation's NRI Matrix — what its row
 * calculator above shows. Mounted only while the card is open, like the NRI
 * Matrix popup, which shares the same matrix atom.
 *
 * @param {object} props - Component props.
 * @param {number} props.nriCalculationId - The NRI calculation.
 * @param {CalculationType} props.calculationType - NRI or WI.
 * @return {JSX.Element} The GIS row's calculation.
 */
function GisCalculationContent({
  nriCalculationId,
  calculationType,
}: Omit<GisCalculationCardProps, 'renderTrigger'>) {
  const setNriCalculationId = useSetAtom(nriCalculationIdForMatrixAtom);
  const matrix = useAtomValue(nriMatrixLoadableAtom);

  useEffect(() => {
    setNriCalculationId(nriCalculationId);
    return () => {
      setNriCalculationId(null);
    };
  }, [nriCalculationId, setNriCalculationId]);

  if (matrix.state === 'loading') return <div>Loading...</div>;

  const data = matrix.state === 'hasData' ? matrix.data : null;
  const gisRow = data ? gisRowOf(data.rows ?? []) : undefined;
  const interestValue =
    (calculationType === 'WI' ? gisRow?.workingInterest : gisRow?.nri) ?? null;
  if (!data || !gisRow || interestValue === null) {
    return <div>Error loading {calculationType} calculation</div>;
  }

  return (
    <UatCalculationContent
      interestValue={interestValue}
      formulaInputs={data.formulaInputs ?? null}
      allocationMethod={gisRow.allocationMethod}
      source="gis"
      rowValue={gisRow.value}
      calculationType={calculationType}
    />
  );
}

/**
 * "Show Calculation" for a GIS NRI / GIS WI value beside an AM value: the GIS
 * row of the manager's allocation method, rather than the stored (in-use)
 * calculation `LazyUnitInterestCard` shows.
 *
 * @param {GisCalculationCardProps} props - Component props.
 * @return {JSX.Element} The card.
 */
export function GisCalculationCard({
  nriCalculationId,
  calculationType,
  renderTrigger,
}: GisCalculationCardProps) {
  return (
    <BaseCard
      contentClassName={styles.calculationCardContent}
      renderTrigger={renderTrigger}
      content={
        <GisCalculationContent
          nriCalculationId={nriCalculationId}
          calculationType={calculationType}
        />
      }
    />
  );
}
